import type { ReactNode } from "react";
import { LockIcon } from "~/components/icons";

type PremiumLockProps = {
  /** Whether the feature is locked (user doesn't have premium) */
  isLocked: boolean;
  /** Content to render */
  children: ReactNode;
  /** Optional custom class for the wrapper */
  className?: string;
  /** Show lock icon (default: true) */
  showLockIcon?: boolean;
  /** Lock icon position (default: "left") */
  lockIconPosition?: "left" | "right";
  /** Callback to open premium modal */
  onOpenPremiumModal?: () => void;
  /** Additional onClick handler */
  onClick?: () => void;
};

/**
 * Wrapper component that adds premium lock behavior to any element.
 * When locked:
 * - Applies grayed out styling
 * - Shows a lock icon
 * - Opens premium modal on click
 *
 * Usage:
 * ```tsx
 * <PremiumLock isLocked={!isPremiumUser} onOpenPremiumModal={openModal}>
 *   <Button>Premium Feature</Button>
 * </PremiumLock>
 * ```
 */
export function PremiumLock({
  isLocked,
  children,
  className = "",
  showLockIcon = true,
  lockIconPosition = "left",
  onOpenPremiumModal,
  onClick,
}: PremiumLockProps) {
  const handleClick = (e: React.MouseEvent) => {
    if (isLocked) {
      e.preventDefault();
      e.stopPropagation();
      onOpenPremiumModal?.();
    }
    onClick?.();
  };

  if (!isLocked) {
    return <>{children}</>;
  }

  return (
    <div
      className={`premium-locked relative flex cursor-pointer items-center ${className}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleClick(e as unknown as React.MouseEvent);
        }
      }}
    >
      {showLockIcon && lockIconPosition === "right" && (
        <LockIcon
          className="me-1.5 h-3.5 w-3.5 flex-shrink-0"
          color="currentColor"
        />
      )}
      <div className="premium-locked-content opacity-50">{children}</div>
      {showLockIcon && lockIconPosition === "left" && (
        <LockIcon
          className="ms-1.5 h-3.5 w-3.5 flex-shrink-0"
          color="currentColor"
        />
      )}
    </div>
  );
}

/**
 * Inline lock icon that can be added next to any text/element
 * Use with the locked styling classes from usePremiumAccess
 */
export function PremiumLockIcon({ className = "" }: { className?: string }) {
  return (
    <LockIcon
      className={`h-3.5 w-3.5 flex-shrink-0 ${className}`}
      color="currentColor"
    />
  );
}

/**
 * CSS classes for premium locked state
 * Can be applied directly to elements for styling only (without redirect behavior)
 */
export const PREMIUM_LOCKED_CLASSES = {
  /** Base locked styling - grayed out with pointer cursor */
  base: "opacity-50 text-gray-500 cursor-pointer",
  /** Locked text styling */
  text: "text-gray-500",
  /** Locked icon/image styling */
  icon: "opacity-50",
  /** Container for locked items with flex layout */
  container: "premium-locked flex items-center",
} as const;

export default PremiumLock;
